<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});
const route = useRoute();
const { t } = useI18n();

const search = ref('');

const pageQuery = gql`
  query ($routeId: String!) {
    page(route_id: $routeId) {
      breadcrumbs {
        id
        name
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      content
      children {
        id
        name
        image
        perex
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      documents {
        name
        type
        file {
          url
        }
      }
      contacts {
        id
        name
        job
        slug: seo_url_slug {
          cs
          en
        }
        image
        group {
          name
        }
        building {
          id
          name
          seo_url_slug {
            cs
            en
          }
        }
      }
      events {
        id
        name
        type
        categories {
          name
        }
        slug: seo_url_slug {
          cs
          en
        }
        image: image_list {
          url
          width
          height
        }
        imageCrop: image_list_crop_to
        buildings {
          id
          name
          seo_url_slug {
            cs
            en
          }
        }
        dates {
          id
          start_at {
            timestamp
          }
          end_at {
            timestamp
          }
        }
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery, { routeId: route.name });
const page = computed(() => data.value?.page);

const archivedDocumentsQuery = gql`
  query {
    documents(types: "study-material", filterInCurrentExhibitions: false) {
      image_preview
      name
      type
      subtitle
      description
      image_preview
      fond_mark
      fond_time_range
      fond_bm
      fond_description
      file {
        id
        url
      }
    }
  }
`;
const { data: archived } = await useAsyncQuery(archivedDocumentsQuery);

const archivedDocuments = computed(() => archived.value.documents);

const documentsQuery = gql`
  query ($search: String) {
    documents(types: "study-material", filterInCurrentExhibitions: true, filterSearch: $search) {
      image_preview
      name
      type
      subtitle
      description
      image_preview
      fond_mark
      fond_time_range
      fond_bm
      fond_description
      file {
        id
        url
      }
    }
  }
`;
const { data: results } = await useAsyncQuery(documentsQuery, { search: search.value });
const documents = computed(() => results.value.documents);

const breadcrumbs = useBreadCrumbs(data.value?.page.breadcrumbs || []);

useHead({
  title: page.value ? (page.value?.seo_meta_title ? page.value?.seo_meta_title : page.value?.name) : null,
  description: page.value && page.value?.seo_meta_description ? page.value?.seo_meta_description : null,
  keywords: page.value && page.value?.seo_meta_keywords ? page.value?.seo_meta_keywords : null,
});

const simplifySearchString = (str) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const filteredArchivedDocuments = computed(() => {
  return archivedDocuments.value.filter((item) => simplifySearchString(item.name).includes(simplifySearchString(search.value)));
});

const filteredDocuments = computed(() => {
  return documents.value.filter((item) => simplifySearchString(item.name).includes(simplifySearchString(search.value)));
});
</script>

<template>
  <main v-if="page" class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <div v-if="page" class="container-max-content">
      <h1 class="heading">{{ page.name }}</h1>
      <DynamicPageContent v-if="page.content" :content="page.content" class="mt-5" />

      <ChildrenList v-if="page.children.length" :children="page.children" />
      <ContactList v-if="page.contacts.length" :contacts="page.contacts" />
      <DocumentList v-if="page.documents.length" :documents="page.documents" />

      <div v-if="page.events.data" class="mt-5">
        <h2 class="heading heading--2 heading--thin">
          {{ t('pages.news.detail.events') }}
        </h2>

        <EventGrid :data="page.events.data" class="mt-4" hide-type />
      </div>

      <div class="mt-4">
        <label for="search" class="hidden">{{ t('pages.education.teachers.search') }}</label>
        <input id="search" v-model="search" type="text" class="input input--search" :placeholder="t('pages.education.teachers.search')" />
      </div>

      <h3 v-if="filteredDocuments?.length" class="heading--3 mt-5">{{ t('pages.education.teachers.archive-title') }}</h3>
      <FileGrid v-if="filteredDocuments?.length" :data="filteredDocuments" class="mt-3" />

      <h3 class="heading--3 mt-6">{{ t('pages.education.teachers.archive-old-title') }}</h3>
      <FileGrid v-if="filteredArchivedDocuments?.length" :data="filteredArchivedDocuments" class="mt-3" />
      <p v-else class="mt-3" v-html="t('global.search.term-not-found', { term: '<strong>' + search + '</strong>' })" />
    </div>
  </main>
</template>
